import { DAY, HOUR, MICROSECOND, MILLISECOND, MINUTE, MONTH, SECOND, WEEK, YEAR } from '@/utilities/date-time/constants'

/**
 * Helper function to round a value and return both the formatted label and the numeric result
 * @private
 * @param {number} value - The value to round
 * @param {number} fixedDigits - Number of decimal places for the label
 * @returns {{label: string, result: number}} Object containing formatted label and numeric result
 */
function roundWithFixed(value: number, fixedDigits: number) {
  const label = value.toFixed(fixedDigits)
  const result = fixedDigits <= 0 ? Math.round(value) : value
  return { label, result }
}

/**
 * Duration label mappings for different formatting styles
 * @private
 */
const DURATION_LABELS = {
  y: 'y',
  yr: 'yr',
  mo: 'mo',
  w: 'w',
  wk: 'wk',
  d: 'd',
  h: 'h',
  hr: 'hr',
  m: 'm',
  min: 'min',
  s: 's',
  ms: 'ms',
  us: 'μs'
}

/**
 * Formats a duration in seconds into a human-readable string representation.
 * Automatically selects the most appropriate unit (years, months, weeks, days, hours, minutes, seconds, milliseconds, or microseconds).
 *
 * @param {number} seconds - The duration in seconds to format. Can be negative.
 * @param {number} [fixedDigits=0] - Number of decimal places to display in the result (0-6).
 * @param {boolean} [abbreviation=false] - Use abbreviated format (e.g., "hr" instead of "hours", "min" instead of "minutes").
 * @param {boolean} [extraShort=false] - Use extra short format (e.g., "h" instead of "hr", "m" instead of "min"). Takes precedence over abbreviation.
 * @param {boolean} [absolute=false] - Use absolute value, removing any negative sign from the result.
 * @param {number} [minimumUnit=MILLISECOND] - The minimum unit to display. Forces the result to be shown in at least this unit, even for smaller values.
 *
 * @returns {string} A formatted duration string.
 *
 * @example
 * // Basic usage
 * getDuration(3661) // "1 hour 1 minute"
 * getDuration(86400) // "1 day"
 * getDuration(1.5) // "2 seconds"
 *
 * @example
 * // With precision
 * getDuration(3661.5, 1) // "1.0 hour 1.5 minutes"
 * getDuration(0.5, 2) // "500.00 milliseconds"
 *
 * @example
 * // Different formats
 * getDuration(3661, 0, true) // "1hr 1min" (abbreviated)
 * getDuration(3661, 0, false, true) // "1h 1m" (extra short)
 *
 * @example
 * // Negative values
 * getDuration(-3661) // "-1 hour -1 minute"
 * getDuration(-3661, 0, false, false, true) // "1 hour 1 minute" (absolute)
 *
 * @example
 * // Minimum unit constraints
 * getDuration(30, 2, false, false, false, HOUR) // "0.01 hours" (forced to hours)
 * getDuration(0.5, 0, false, false, false, SECOND) // "1 second" (forced to seconds minimum)
 *
 * @example
 * // Large time periods
 * getDuration(31536000) // "1 year"
 * getDuration(2629800, 0, true) // "1mo"
 * getDuration(604800, 0, false, true) // "1w"
 */
export default function getDuration(
  seconds: number,
  fixedDigits = 0,
  abbreviation = false,
  extraShort = false,
  absolute = false,
  minimumUnit = MILLISECOND
): string {
  const absValue = Math.abs(seconds * 1000)
  const msValue = absolute ? absValue : seconds * 1000

  // YEARS
  if (absValue >= YEAR || minimumUnit === YEAR) {
    const { label, result } = roundWithFixed(msValue / YEAR, fixedDigits)
    if (extraShort) {
      return `${label}${DURATION_LABELS.y}`
    }
    if (abbreviation) {
      return `${label}${DURATION_LABELS.yr}`
    }
    const unit = result === 1 ? 'year' : 'years'
    return `${label} ${unit}`
  }

  // MONTHS
  if (absValue >= MONTH || minimumUnit === MONTH) {
    const { label, result } = roundWithFixed(msValue / MONTH, fixedDigits)
    if (extraShort) {
      return `${label}${DURATION_LABELS.m}`
    }
    if (abbreviation) {
      return `${label}${DURATION_LABELS.mo}`
    }
    const unit = result === 1 ? 'month' : 'months'
    return `${label} ${unit}`
  }

  // WEEKS
  if (absValue >= WEEK || minimumUnit === WEEK) {
    const { label, result } = roundWithFixed(msValue / WEEK, fixedDigits)
    if (extraShort) {
      return `${label}${DURATION_LABELS.w}`
    }
    if (abbreviation) {
      return `${label}${DURATION_LABELS.wk}`
    }
    const unit = result === 1 ? 'week' : 'weeks'
    return `${label} ${unit}`
  }

  // DAYS
  if (absValue >= DAY || minimumUnit === DAY) {
    const { label, result } = roundWithFixed(msValue / DAY, fixedDigits)
    if (extraShort || abbreviation) {
      return `${label}${DURATION_LABELS.d}`
    }
    const unit = result === 1 ? 'day' : 'days'
    return `${label} ${unit}`
  }

  // HOURS
  if (absValue >= HOUR || minimumUnit === HOUR) {
    const { label, result } = roundWithFixed(msValue / HOUR, fixedDigits)
    if (extraShort) {
      return `${label}${DURATION_LABELS.h}`
    }
    if (abbreviation) {
      return `${label}${DURATION_LABELS.hr}`
    }
    const unit = result === 1 ? 'hour' : 'hours'
    return `${label} ${unit}`
  }

  // MINUTES
  if (absValue >= MINUTE || minimumUnit === MINUTE) {
    const { label, result } = roundWithFixed(msValue / MINUTE, fixedDigits)
    if (extraShort) {
      return `${label}${DURATION_LABELS.m}`
    }
    if (abbreviation) {
      return `${label}${DURATION_LABELS.min}`
    }
    const unit = result === 1 ? 'minute' : 'minutes'
    return `${label} ${unit}`
  }

  // SECONDS
  if (absValue >= SECOND || minimumUnit === SECOND) {
    const { label, result } = roundWithFixed(msValue / SECOND, fixedDigits)
    if (extraShort || abbreviation) {
      return `${label}${DURATION_LABELS.s}`
    }
    const unit = result === 1 ? 'second' : 'seconds'
    return `${label} ${unit}`
  }

  // MILLISECONDS
  if (absValue >= MILLISECOND || minimumUnit === MILLISECOND) {
    const { label, result } = roundWithFixed(msValue / MILLISECOND, fixedDigits)
    if (extraShort || abbreviation) {
      return `${label}${DURATION_LABELS.ms}`
    }
    const unit = result === 1 ? 'millisecond' : 'milliseconds'
    return `${label} ${unit}`
  }

  // MICROSECONDS (fallback)
  const { label, result } = roundWithFixed(msValue / MICROSECOND, fixedDigits)
  if (extraShort || abbreviation) {
    return `${label}${DURATION_LABELS.us}`
  }
  const unit = result === 1 ? 'microsecond' : 'microseconds'
  return `${label} ${unit}`
}
